<script lang="ts" setup>
import { watch, ref } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import SettingsTable from '@/pages/SettingsPage/SettingsTable/SettingsTable.vue'
import cloneDeep from 'clone-deep'
import ChatMessageTemplateTableRow from '@/pages/SettingsPage/SettingsPageChatMessageTemplateView/ChatMessageTemplateTableRow.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'

const emit = defineEmits<{
  (event: 'save', data: ChatMessageTemplateTableForm): void
}>()

const props = defineProps<{
  form: ChatMessageTemplateTableForm
}>()

export type ChatMessageTemplateTableForm = {
  label: string
  body: string
  selections: string[]
}

const TEXTAREA_PLACEHOLDER = `テンプレートの本文を入力してください。
例）
ご連絡いただきありがとうございます。
宜しければ以下のリンクから日程調整をさせていただければと思います。
【日程調整】
リンク
`

const initialForm = cloneDeep(props.form)

const formRef = ref<ChatMessageTemplateTableForm>(initialForm)

watch(
  () => props.form,
  newForm => {
    formRef.value = cloneDeep(newForm)
  },
  { immediate: true }
)

watch(
  () => formRef.value,
  value => {
    if (value.body || value.label) {
      validateErrors()
    }
  }
)

const submitting = ref(false)

const saveTemplate = async () => {
  submitting.value = true
  validateErrors()

  const hasError = Object.values(formErrors.value).some(
    error => error !== undefined
  )

  if (hasError) {
    submitting.value = false
    return
  }

  const submittedForm: ChatMessageTemplateTableForm = {
    ...formRef.value,
    selections: formRef.value.selections.filter(item => item !== ''),
  }

  emit('save', submittedForm)
  submitting.value = false
}

const validateErrors = () => {
  formErrors.value = {}

  if (!formRef.value.label) {
    formErrors.value.label = 'タイトルを入力してください'
  }
  if (!formRef.value.body) {
    formErrors.value.body = 'テンプレート本文を入力してください'
  }
}

const formErrors = ref<Record<string, string | undefined>>({})
</script>

<template>
  <section :class="$style.container">
    <SettingsTable :class="$style.table" :isEditing="true">
      <ChatMessageTemplateTableRow name="タイトル">
        <template #editor>
          <DolphinTextInput
            type="text"
            v-model="formRef.label"
            :class="$style.textInput"
            :invalid="!!formErrors.label"
          />
          <p :class="$style.error">{{ formErrors.label }}</p>
        </template>
      </ChatMessageTemplateTableRow>
      <ChatMessageTemplateTableRow name="テンプレート本文">
        <template #editor>
          <DolphinTextarea
            :rows="10"
            v-model="formRef.body"
            :invalid="!!formErrors.body"
            :placeholder="TEXTAREA_PLACEHOLDER"
          />
          <p :class="$style.error">{{ formErrors.body }}</p>
        </template>
      </ChatMessageTemplateTableRow>
      <ChatMessageTemplateTableRow name="">
        <template #editor>
          <MessageAttachmentActions
            v-model="formRef.selections"
            :invalid="!!formErrors.selections"
          />
          <p :class="$style.error">{{ formErrors.selections }}</p>
        </template>
      </ChatMessageTemplateTableRow>
    </SettingsTable>
    <div :class="$style.saveWrapper">
      <FlatButton size="large" @click="saveTemplate" :disabled="submitting"
        >この内容で保存する
      </FlatButton>
    </div>
  </section>
</template>

<style lang="scss" module>
.container {
  &__footer {
    &:nth-child(n + 2) {
      padding-top: 24px;
      border-top: 1px solid $color-border-low-emphasis;
      margin-top: 40px;
    }
  }
}

.table {
  display: grid;
  row-gap: 20px;
}

.textInput {
  width: 100%;
}

.saveWrapper {
  display: flex;
  margin-top: 24px;
  justify-content: center;
}

.error {
  color: $color-caution-accent;
  font-weight: 700;
  margin-top: 8px;
}
</style>
